import type {FC, ReactNode} from 'react'
import type {Path as RPath} from 'ramda'
import type {HighlightedRect} from './mask'

import {Fragment, useRef, useEffect, useCallback, useMemo} from 'react'
import {atom} from 'jotai'
import {useUpdateAtom, useAtomValue} from 'jotai/utils'
import {pathOr, lensPath, view, set, assoc, dissoc, without} from 'ramda'

import {ClipMask} from './mask'

export type ProgressDerivedAction =
  | 'next'
  | 'prev'
  | 'cancel'
  | 'reset'
  | 'start'

export type ProgressDerivedArgs = [string, ProgressDerivedAction]

export type StepEvent = 'enter' | 'leave'

type Callback = () => void

export type EventRegistry = Record<
  string,
  Record<string, Record<StepEvent, Callback[]>>
>

export const namedBoundingRectsAtom = atom<Record<string, HighlightedRect>>({})

export const progressRegistryAtom = atom<Record<string, string[]>>({})

export const progressStateAtom = atom<Record<string, number>>({})

export const eventbusAtom = atom<EventRegistry>({})

const runCallbacks = (path: RPath, registry: EventRegistry) => {
  const functions: Callback[] = pathOr([], path, registry)
  functions.forEach((cb) => cb())
}

export const namedRefsAtom = atom<Record<string, React.RefObject<HTMLElement>>>(
  {}
)

const progressDerivedAtom = atom(
  null,
  (get, set, args: ProgressDerivedArgs) => {
    const [name, action] = args
    const progress = get(progressStateAtom)[name]
    const steps = get(progressRegistryAtom)[name]
    const refs = get(namedRefsAtom)
    if (!steps) {
      return
    }
    const prev = progress === undefined ? -1 : progress
    const callbacks = get(eventbusAtom)
    let val: number
    switch (action) {
      case 'next':
        {
          const val = prev < steps.length - 1 ? prev + 1 : -1
          const nextScene = steps[val]
          // we try access the DOM element and update the bounding client rect before go next.
          // that's because it may incorrect because we may got incorrect rect at first time
          // because the DOM element still in animation.
          if (nextScene) {
            const ref = refs[nextScene]
            if (ref && ref.current) {
              const rect = ref.current.getBoundingClientRect()
              set(namedBoundingRectsAtom, (prev) =>
                assoc(nextScene, rect, prev)
              )
            }
          }
          runCallbacks([name, steps[prev] || '', 'leave'], callbacks)
          set(progressStateAtom, (exists) => ({...exists, [name]: val}))
          runCallbacks([name, nextScene || '', 'enter'], callbacks)
          if (val === -1) {
            runCallbacks([name, '', 'finish'], callbacks)
          }
        }
        break

      // @fixme no tested.
      case 'prev':
        val = prev > 0 ? prev - 1 : -1
        set(progressStateAtom, (exists) => ({...exists, [name]: val}))
        break

      // @fixme no tested.
      case 'cancel':
        set(progressStateAtom, (exists) => ({...exists, [name]: -1}))
        runCallbacks([name, '', 'finish'], callbacks)
        break

      // @fixme no tested.
      case 'reset':
      case 'start':
        set(progressStateAtom, (exists) => ({...exists, [name]: 0}))
        break

      default:
        break
    }
  }
)

export function useNamedBoundingRectRef<T extends HTMLElement>(name: string) {
  const ref = useRef<T>(null)
  const setBoundingRect = useUpdateAtom(namedBoundingRectsAtom)
  const setNamedRefs = useUpdateAtom(namedRefsAtom)
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null
    let intersectionObserver: IntersectionObserver | null = null
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setBoundingRect((prev) => ({
        ...prev,
        [name]: rect,
      }))

      intersectionObserver = new IntersectionObserver((entries) => {
        if (entries && entries.length && entries[0]) {
          const rect = entries[0].boundingClientRect
          setBoundingRect((prev) => ({
            ...prev,
            [name]: rect,
          }))
        }
      })
      intersectionObserver.observe(ref.current)

      resizeObserver = new ResizeObserver((entries) => {
        if (entries && entries.length && entries[0]) {
          const rect = entries[0].contentRect
          setBoundingRect((prev) => ({
            ...prev,
            [name]: rect,
          }))
        }
      })
      resizeObserver.observe(ref.current)
    }
    setNamedRefs((prev) => assoc(name, ref, prev))
    return () => {
      if (intersectionObserver) {
        intersectionObserver.disconnect()
      }
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      setNamedRefs((prev) => dissoc(name, prev))
    }
  }, [name, ref, setBoundingRect, setNamedRefs])

  return ref
}

export const useProgressControl = (name: string) => {
  const dispatch = useUpdateAtom(progressDerivedAtom)
  return useCallback(
    (action: ProgressDerivedAction) => {
      dispatch([name, action])
    },
    [dispatch, name]
  )
}

export const useCurrentStep = (name: string) => {
  const anAtom = useMemo(() => {
    return atom((get) => {
      const idx = get(progressStateAtom)[name]
      const steps = get(progressRegistryAtom)[name]
      if (!steps || idx === -1 || idx === undefined) {
        return undefined
      }
      return steps[idx]
    })
  }, [name])
  return useAtomValue(anAtom)
}

export const useProgressHighlightedRect = (name: string) => {
  const anAtom = useMemo(() => {
    return atom((get) => {
      const idx = get(progressStateAtom)[name]
      const steps = get(progressRegistryAtom)[name]
      if (!steps || idx === -1 || idx === undefined) {
        return undefined
      }
      const stepName = steps[idx]
      if (!stepName) {
        return undefined
      }
      return get(namedBoundingRectsAtom)[stepName]
    })
  }, [name])
  return useAtomValue(anAtom)
}

const useCallbackRegister = (
  name: string,
  when: string,
  eventName: string,
  fn?: Callback
) => {
  const setCallbacks = useUpdateAtom(eventbusAtom)
  useEffect(() => {
    const lens = lensPath([name, when, eventName])
    if (fn) {
      setCallbacks((prev) => {
        const exists: Callback[] = view(lens, prev) || []
        if (exists.indexOf(fn) === -1) {
          return set(lens, [...exists, fn], prev)
        }
      })
    }
    return () => {
      if (fn) {
        setCallbacks((prev) => {
          const exists: Callback[] = view(lens, prev) || []
          if (exists.indexOf(fn) !== -1) {
            return set(lens, without([fn], exists), prev)
          }
        })
      }
    }
  }, [name, when, fn, eventName, setCallbacks])
}

export const ProgressiveMask: FC<{
  name: string
  steps: string[]
  onFinish?: Callback
  children?: ReactNode
}> = ({name, steps, onFinish, children}) => {
  const setProgressRegistery = useUpdateAtom(progressRegistryAtom)

  useEffect(() => {
    setProgressRegistery((prev) => ({
      ...prev,
      [name]: steps,
    }))
  }, [name, steps, setProgressRegistery])

  const highlightedAreaRect = useProgressHighlightedRect(name)
  const trigger = useProgressControl(name)

  useCallbackRegister(name, '', 'finish', onFinish)

  if (!highlightedAreaRect) {
    return <Fragment>{children}</Fragment>
  }
  return (
    <Fragment>
      <ClipMask rect={highlightedAreaRect} onClick={() => trigger('next')} />
      {children && children}
    </Fragment>
  )
}

export const StepControl: FC<{
  name: string
  when: string
  onEnter?: Callback
  onLeave?: Callback
  children: (
    rect: HighlightedRect,
    dispatch: (action: ProgressDerivedAction) => void
  ) => ReactNode
}> = ({name, when, onEnter, onLeave, children}) => {
  const step = useCurrentStep(name)
  const rect = useProgressHighlightedRect(name)
  const dispatch = useProgressControl(name)
  useCallbackRegister(name, when, 'enter', onEnter)
  useCallbackRegister(name, when, 'leave', onLeave)
  if (step !== when || !rect) {
    return null
  }
  return <Fragment>{children(rect, dispatch)}</Fragment>
}

export const Hint: FC<{
  name: string
  onEnter?: Callback
  onLeave?: Callback
  onFinish?: Callback
  children: (
    rect: HighlightedRect,
    dispatch: (action: ProgressDerivedAction) => void
  ) => ReactNode
}> = ({name, onEnter, onLeave, onFinish, children}) => (
  <ProgressiveMask name={name} steps={[name]} onFinish={onFinish}>
    <StepControl name={name} when={name} onEnter={onEnter} onLeave={onLeave}>
      {children}
    </StepControl>
  </ProgressiveMask>
)
